import { useEffect, useRef, useState } from 'react';
import { AGENT_STARTED_MS, agentStartedAnnouncement } from './island-agent-started';
import { useIslandState } from './use-island-state';
import { useIslandQuickView } from './use-island-quickview';
import { useMyAgents } from './my-agents';
import { useAgentWorkspaceTasks } from './agent-workspace-tasks';
import { useProductName } from './use-brand';

/**
 * The island's transient "{agentName} started working" name, or null. Watches the GLOBAL in-motion count and
 * announces when it RISES — the rule lives in the pure `agentStartedAnnouncement`; this hook only supplies
 * the baseline, the naming data and the 5s window.
 *
 * The baseline starts unset, so the FIRST count this browser sees seeds silently: whatever is already running
 * when the app loads never announces itself, and a remount (a route change) is likewise quiet rather than
 * replaying an old start.
 */
export function useIslandAgentStarted(): string | null {
  const island = useIslandState();
  const tasksState = useAgentWorkspaceTasks();
  const quickView = useIslandQuickView();
  const teamState = useMyAgents();
  const productName = useProductName();
  const [name, setName] = useState<string | null>(null);
  const seen = useRef<number | null>(null);

  // The naming data is read through a ref rather than the effect's deps ON PURPOSE: both arrays are rebuilt
  // every render, so depending on them would re-run the effect constantly — and its cleanup would clear the
  // 5s timer, cutting the announcement short on the next render. The count is the only real trigger.
  const naming = useRef<{ tasks: Parameters<typeof agentStartedAnnouncement>[0]['tasks']; agents: { id?: string; name?: string }[] }>({
    tasks: [],
    agents: [],
  });
  naming.current = {
    tasks: quickView.status === 'ready' ? quickView.data.sorted : [],
    agents: teamState.status === 'ready' ? teamState.team.agents : [],
  };

  // Only observe once the COUNTS are in, not merely once the aggregate reports ready: `ready` means the
  // workspace list resolved, and the per-workspace task reads it is still waiting on read as zero. Seeding
  // the baseline off that zero makes the first real load a rise, which announced "{agent} started working"
  // on every plain refresh of an account that simply had work in flight.
  const ready = tasksState.status === 'ready' && tasksState.data.settled;
  const inMotionCount = island.inMotionCount;

  useEffect(() => {
    if (!ready) return undefined;
    const { name: announced, count } = agentStartedAnnouncement({
      prevInMotionCount: seen.current,
      inMotionCount,
      tasks: naming.current.tasks,
      agents: naming.current.agents,
      // The Maestro is the fallback when the newest in-progress task resolves to no roster name.
      mainAgentName: productName,
    });
    seen.current = count;
    if (!announced) return undefined;
    setName(announced);
    const id = window.setTimeout(() => setName(null), AGENT_STARTED_MS);
    return () => window.clearTimeout(id);
  }, [ready, inMotionCount, productName]);

  return name;
}
